import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, FileText, Clock, CheckCircle, DollarSign, Printer } from 'lucide-react';
import { orderService } from '../api/orders';
import { encounterService } from '../api/encounters';
import { patientService, invoiceService } from '../../../lib/services';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor, can, getUserRole } from '../../../lib/rbac';
import { testPriceMap } from '../data/testCatalogs';
import { generateLabReportPDF } from '../../../lib/pdf';
import toast from 'react-hot-toast';
import type { Order, Encounter } from '../types';
import type { Patient } from '../../../lib/supabase';

interface OrderWithDetails extends Order {
  encounter?: Encounter;
  patient?: Patient;
  invoiceId?: string;
}

export default function Orders() {
  const { user } = useStore();
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'collected' | 'processing' | 'completed'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'lab' | 'radiology'>('all');

  const doctorId = getDoctorIdFromUser(user);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, search, statusFilter, typeFilter]);

  const loadOrders = async () => {
    try {
      const ordersData = await orderService.getAll();
      
      const ordersWithDetails = await Promise.all(
        ordersData.map(async (order) => {
          try {
            const encounter = await encounterService.getById(order.encounterId);
            const patient = await patientService.getById(encounter.patientId);
            return {
              ...order,
              encounter,
              patient,
            };
          } catch (error) {
            return order;
          }
        })
      );

      let filteredOrders = ordersWithDetails;
      if (isDoctor(user) && doctorId) {
        filteredOrders = ordersWithDetails.filter(order => 
          (order as OrderWithDetails).encounter?.doctorId === doctorId
        );
      }

      setOrders(filteredOrders);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(order =>
        order.patient?.name.toLowerCase().includes(searchLower) ||
        order.items.some(item => 
          item.name.toLowerCase().includes(searchLower) ||
          item.code?.toLowerCase().includes(searchLower)
        )
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(order => order.type === typeFilter);
    }

    setFilteredOrders(filtered);
  };

  const handleStatusUpdate = async (orderId: string, newStatus: 'pending' | 'collected' | 'processing' | 'completed') => {
    try {
      await orderService.updateStatus(orderId, newStatus);
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      setFilteredOrders(filteredOrders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      toast.success(`Order status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const handleProcessBilling = async (order: OrderWithDetails) => {
    try {
      const invoiceItems = order.items.map(item => ({
        name: `${order.type.toUpperCase()} • ${item.name}`,
        qty: 1,
        unitPrice: testPriceMap[item.code || ''] || 0,
        total: testPriceMap[item.code || ''] || 0
      }));

      const subtotal = invoiceItems.reduce((sum, item) => sum + item.total, 0);
      const taxRate = 0.08;
      const taxAmount = subtotal * taxRate;
      const total = subtotal + taxAmount;

      const invoice = await invoiceService.create({
        patient_id: order.patient?.id || '',
        invoice_number: `INV-${Date.now()}`,
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        amount_paid: 0,
        balance_due: total,
        status: 'sent' as const,
        notes: `Lab order #${order.id}`,
        discount_amount: 0
      });

      await (orderService as any).linkInvoice(order.id, invoice.id);
      
      setOrders(orders.map(o => 
        o.id === order.id ? { ...o, invoiceId: invoice.id } : o
      ));
      setFilteredOrders(filteredOrders.map(o => 
        o.id === order.id ? { ...o, invoiceId: invoice.id } : o
      ));

      toast.success('Invoice created successfully');
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handlePrintReport = (order: OrderWithDetails) => {
    if (!order.patient || !order.encounter) {
      toast.error('Missing patient or encounter data');
      return;
    }
    generateLabReportPDF(order, order.patient, order.encounter);
  };

  const getStatusIcon = (status: string) => {
    return status === 'completed' ? (
      <CheckCircle className="w-4 h-4 text-green-600" />
    ) : (
      <Clock className="w-4 h-4 text-yellow-600" />
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'collected': return 'bg-purple-100 text-purple-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    return priority === 'urgent' 
      ? 'bg-red-100 text-red-800' 
      : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-sm text-neutral-600">Manage lab and radiology orders</p>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Total Orders</p>
              <p className="text-2xl font-bold text-neutral-900">{orders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Pending</p>
              <p className="text-2xl font-bold text-neutral-900">
                {orders.filter(o => o.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Processing</p>
              <p className="text-2xl font-bold text-neutral-900">
                {orders.filter(o => o.status === 'processing').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Completed</p>
              <p className="text-2xl font-bold text-neutral-900">
                {orders.filter(o => o.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-600">Urgent</p>
              <p className="text-2xl font-bold text-neutral-900">
                {orders.filter(o => o.priority === 'urgent').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'collected' | 'processing' | 'completed')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="collected">Collected</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="lab">Lab Tests</option>
              <option value="radiology">Radiology</option>
            </select>
          </div>

          <div className="flex items-center text-sm text-gray-600">
            <Filter className="w-4 h-4 mr-2" />
            {filteredOrders.length} of {orders.length} orders
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="p-6 text-center text-neutral-500">
            No orders found matching your criteria
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.patient?.name || 'Unknown Patient'}
                      </div>
                      {order.patient?.email && (
                        <div className="text-sm text-gray-500">{order.patient.email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {order.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {order.items.map((item, index) => (
                          <div key={index} className="text-sm text-gray-900">
                            {item.code && <span className="font-mono mr-2">{item.code}</span>}
                            {item.name}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                        {order.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(order.status)}
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {can(getUserRole(user), 'manage_order_status' as any) && (
                          <>
                            {order.status === 'pending' && (
                              <button
                                onClick={() => handleStatusUpdate(order.id, 'collected')}
                                className="text-blue-600 hover:text-blue-900"
                                title="Mark as Collected"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            {order.status === 'collected' && (
                              <button
                                onClick={() => handleStatusUpdate(order.id, 'processing')}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Mark as Processing"
                              >
                                <Clock className="w-4 h-4" />
                              </button>
                            )}
                            {order.status === 'processing' && (
                              <button
                                onClick={() => handleStatusUpdate(order.id, 'completed')}
                                className="text-green-600 hover:text-green-900"
                                title="Mark as Completed"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                        
                        {can(getUserRole(user), 'process_order_billing' as any) && !(order as any).invoiceId && (
                          <button
                            onClick={() => handleProcessBilling(order)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Process Billing"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        )}
                        
                        {(order as any).invoiceId && (
                          <Link
                            to={`/billing/invoices/${(order as any).invoiceId}`}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Invoice"
                          >
                            <FileText className="w-4 h-4" />
                          </Link>
                        )}
                        
                        {order.status === 'completed' && can(getUserRole(user), 'print_lab_reports' as any) && (
                          <button
                            onClick={() => handlePrintReport(order)}
                            className="text-green-600 hover:text-green-900"
                            title="Print Lab Report"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                        )}

                        <Link
                          to={`/orders/${order.id}`}
                          className="text-gray-600 hover:text-gray-900"
                          title="View Details"
                        >
                          <FileText className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
